<template>
  <div v-if="isExporting" class="factory-pdf-export-wrapper">
    <div id="factory-production-pdf-content" class="factory-production-pdf-container">
      <!-- 分页模式：New.vue — DOM 预分页，逐页截图 -->
      <template v-if="paginated">
        <div
          v-for="(page, pageIdx) in items"
          :key="'factory-prod-page-' + pageIdx"
          class="factory-production-pdf-page factory-production-pdf-page--fixed"
        >
          <div class="factory-pdf-header-section">
            <div class="factory-pdf-header">
              <div class="factory-pdf-header-left">
                <div class="factory-pdf-logo-box">
                  <img :src="jfLogo" alt="logo" class="factory-pdf-logo" />
                </div>
                <div class="factory-pdf-header-text">
                  <div class="factory-pdf-company-row">
                    <div class="factory-pdf-company-name">江丰集团</div>
                    <div class="factory-pdf-order-tag">生产单</div>
                  </div>
                  <div class="factory-pdf-order-info">
                    <span style="color: #000;font-weight: bold;">销售单号 &nbsp;&nbsp;</span>
                    <span class="factory-pdf-strong-text">{{ contractInfo.code }}</span>
                  </div>
                </div>
              </div>
              <div class="factory-pdf-header-right">
                <span class="factory-pdf-page-info">第{{ pageIdx + 1 }}页/共{{ totalPages }}页</span>
              </div>
            </div>
            <div class="factory-pdf-base-info">
              <div class="factory-pdf-base-row">
                <div class="factory-pdf-base-item">
                  <span class="label">项目名称</span>
                  <span class="value">{{ contractInfo.contractName || contractInfo.project || '/' }}</span>
                </div>
                <div class="factory-pdf-base-item factory-pdf-base-item-time">
                  <span class="label">客户简称</span>
                  <span class="value">{{ contractInfo.customerShortName || '/' }}</span>
                </div>
              </div>
              <div class="factory-pdf-base-row">
                <div class="factory-pdf-base-item">
                  <span class="label">业务员</span>
                  <span class="value">{{ contractInfo.salesmanName || '/' }}</span>
                </div>
                <div class="factory-pdf-base-item factory-pdf-base-item-time">
                  <span class="label">导出时间</span>
                  <span class="value">{{ exportDateTime }}</span>
                </div>
              </div>
              <div class="factory-pdf-base-row">
                <div class="factory-pdf-base-item">
                  <span class="label">项目类型</span>
                  <span class="value">{{ contractInfo.projectType || '/' }}</span>
                </div>
                <div class="factory-pdf-base-item factory-pdf-base-item-time">
                  <span class="label">生管分配</span>
                  <span class="value">{{ assignTimeDisplay }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="factory-pdf-table-wrapper">
            <table class="factory-pdf-table">
              <thead>
                <tr>
                  <th style="width: 55px;">项次</th>
                  <th style="width: 120px;" class="factory-pdf-text-left">批号/时间</th>
                  <th style="width: 180px;" class="factory-pdf-text-left">产品信息</th>
                  <th style="width: 150px;" class="factory-pdf-text-left">工艺</th>
                  <th style="width: 90px;" class="factory-pdf-text-left">尺寸</th>
                  <th style="width: 70px;">预定交期</th>
                  <th style="width: 80px;">总数</th>
                </tr>
              </thead>
              <tbody
                class="factory-pdf-product-row-group"
                v-for="(row, rowIdx) in page.items"
                :key="'factory-prod-block-' + pageIdx + '-' + rowIdx"
              >
                <tr class="factory-pdf-product-main-row">
                  <td class="text-center">
                    {{ getItemIndexOffset(pageIdx) + rowIdx + 1 }}
                  </td>
                  <td class="factory-pdf-text-left">
                    <div>{{ row.batch }}</div>
                    <div v-if="row.productionAssignTime">{{ formatIntDate(row.productionAssignTime) }}</div>
                  </td>
                  <td class="factory-pdf-text-left">
                    {{ row.code }}
                    <div>{{ row.shortName || row.nameShort }}</div>
                    <div>{{ row.syscode }}</div>
                  </td>
                  <td class="factory-pdf-text-left">
                    <span style="color: #D2475D;" v-if="row.typeOf">{{ row.typeOf }}:</span>
                    <template v-if="row.productionNode && row.productionNode.length">
                      {{ row.productionNode.join(' / ') }}
                    </template>
                    <template v-else>/</template>
                  </td>
                  <td class="text-left">
                    <template v-if="row.width || row.depth || row.height">
                      <div v-if="row.width">W {{ row.width }}</div>
                      <div v-if="row.depth">D {{ row.depth }}</div>
                      <div v-if="row.height">H {{ row.height }}</div>
                    </template>
                    <template v-else>/</template>
                  </td>
                  <td class="text-center">
                    {{ formatIntDate(row.deliveryTime || contractInfo.deliveryTime) }}
                  </td>
                  <td class="text-center">
                    <template v-if="row.count">
                      <template v-if="row.preProduction === 1 || row.preProduction === Number(1)">产前样</template>
                      {{ row.count }}{{ row.unit || '' }}
                    </template>
                    <template v-else>/</template>
                  </td>
                </tr>
                <tr class="factory-pdf-product-desc-row">
                  <td colspan="8" class="factory-pdf-product-desc-cell">
                    {{ row.description }}
                  </td>
                </tr>
              </tbody>
              <tbody v-if="pageIdx === totalPages - 1">
                <tr class="factory-pdf-table-summary-row">
                  <td :colspan="7" class="factory-pdf-table-summary-cell">
                    品项数 {{ totalItems }}
                    <span style="margin-left: 24px;padding-right: 30px;">
                      合计数量 {{ totalCount }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <!-- 非分页模式：factoryManufactureProduct — 单页 DOM + canvas 拼合分页 -->
      <template v-else>
        <div class="factory-production-pdf-page">
          <div class="factory-pdf-header-section">
            <div class="factory-pdf-header">
              <div class="factory-pdf-header-left">
                <div class="factory-pdf-logo-box">
                  <img :src="jfLogo" alt="logo" class="factory-pdf-logo" />
                </div>
                <div class="factory-pdf-header-text">
                  <div class="factory-pdf-company-row">
                    <div class="factory-pdf-company-name">江丰集团</div>
                    <div class="factory-pdf-order-tag">生产单</div>
                  </div>
                  <div class="factory-pdf-order-info">
                    <span style="color: #000;font-weight: bold;">销售单号 &nbsp;&nbsp;</span>
                    <span class="factory-pdf-strong-text">{{ contractInfo.code }}</span>
                  </div>
                </div>
              </div>
              <!-- <div class="factory-pdf-header-right">
                <span class="factory-pdf-page-info">生产单</span>
              </div> -->
            </div>
            <div class="factory-pdf-base-info">
              <div class="factory-pdf-base-row">
                <div class="factory-pdf-base-item">
                  <span class="label">项目名称</span>
                  <span class="value">{{ contractInfo.contractName || contractInfo.project || '/' }}</span>
                </div>
                <div class="factory-pdf-base-item factory-pdf-base-item-time">
                  <span class="label">客户简称</span>
                  <span class="value">{{ contractInfo.customerShortName }}</span>
                </div>
              </div>
              <div class="factory-pdf-base-row">
                <div class="factory-pdf-base-item">
                  <span class="label">业务员</span>
                  <span class="value">{{ contractInfo.salesmanName || '/' }}</span>
                </div>
                <div class="factory-pdf-base-item factory-pdf-base-item-time">
                  <span class="label">导出时间</span>
                  <span class="value">{{ exportDateTime }}</span>
                </div>
              </div>
              <div class="factory-pdf-base-row">
                <div class="factory-pdf-base-item">
                  <span class="label">项目类型</span>
                  <span class="value">{{ contractInfo.projectType }}</span>
                </div>
                <div class="factory-pdf-base-item factory-pdf-base-item-time">
                  <span class="label">生管分配</span>
                  <span class="value">{{ assignTimeDisplay }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="factory-pdf-table-wrapper">
            <table class="factory-pdf-table">
              <thead>
                <tr>
                  <th style="width: 55px;">项次</th>
                  <th style="width: 100px;" class="factory-pdf-text-left">批号/时间</th>
                  <th style="width: 100px;" class="factory-pdf-text-left">产品信息</th>
                  <th style="width: 110px;">区域</th>
                  <th style="width: 150px;" class="factory-pdf-text-left">工艺</th>
                  <th style="width: 80px;" class="factory-pdf-text-left">尺寸</th>
                  <th style="width: 70px;">预定交期</th>
                  <th style="width: 80px;">总数</th>
                </tr>
              </thead>
              <tbody
                class="factory-pdf-product-row-group"
                v-for="(row, rowIdx) in items"
                :key="'factory-prod-block-' + rowIdx"
              >
                <tr class="factory-pdf-product-main-row">
                  <td class="text-center">
                    {{ rowIdx + 1 }}
                  </td>
                  <td class="factory-pdf-text-left">
                    <div>{{ row.batch }}</div>
                    <div v-if="row.productionAssignTime">{{ formatIntDate(row.productionAssignTime) }}</div>
                  </td>
                  <td class="factory-pdf-text-left">
                    {{ row.code }}
                    <div style="word-break: break-all;">{{ row.shortName || row.nameShort }}</div>
                    <div style="word-break: break-all;">{{ row.syscode }}</div>
                  </td>
                  <td class="text-center"><pre style="word-break: break-all;word-wrap: break-word;white-space: pre-wrap;">{{ row.area }}</pre></td>
                  <td class="factory-pdf-text-left">
                    <span style="color: #D2475D;" v-if="row.typeOf">{{ row.typeOf }}:</span>
                    <template v-if="row.productionNode && row.productionNode.length">
                      {{ row.productionNode.join(' / ') }}
                    </template>
                    <template v-else>/</template>
                  </td>
                  <td class="text-left">
                    <template v-if="row.width || row.depth || row.height">
                      <div v-if="row.width">W {{ row.width }}</div>
                      <div v-if="row.depth">D {{ row.depth }}</div>
                      <div v-if="row.height">H {{ row.height }}</div>
                    </template>
                    <template v-else>/</template>
                  </td>
                  <td class="text-center">
                    {{ formatIntDate(row.deliveryTime || contractInfo.deliveryTime) }}
                  </td>
                  <td class="text-center">
                    <template v-if="row.count">
                      <template v-if="row.preProduction === 1 || row.preProduction === Number(1)">产前样</template>
                      {{ row.count }}{{ row.unit || '' }}
                    </template>
                    <template v-else>/</template>
                  </td>
                </tr>
                <tr class="factory-pdf-product-desc-row">
                  <td colspan="8" class="factory-pdf-product-desc-cell">
                    {{ row.description }}
                  </td>
                </tr>
              </tbody>
              <tbody>
                <tr class="factory-pdf-table-summary-row">
                  <td :colspan="8" class="factory-pdf-table-summary-cell">
                    品项数 {{ items.length }}
                    <span style="margin-left: 24px;padding-right: 30px;">
                      合计数量 {{ totalCount }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import jfLogo from '@/assets/jf.png'

interface Props {
  items: any[]
  contractInfo: Record<string, any>
  totalCount: number
  exportDateTime: string
  assignTimeDisplay: string
  /** 分页模式：items 为 { items: any[] }[] 时启用 DOM 预分页逐页截图 */
  paginated?: boolean
  /** 分页模式下所有页的品项总数 */
  totalItems?: number
}
const props = withDefaults(defineProps<Props>(), {
  paginated: false,
  totalItems: 0
})

const emit = defineEmits<{
  success: []
  error: [message: string]
}>()

const isExporting = ref(false)

const totalPages = computed(() => (props.paginated ? props.items.length : 1))

/** 分页模式下计算前 N 页的累积品项数，用于项次列偏移 */
const getItemIndexOffset = (pageIdx: number): number => {
  if (!props.paginated) return 0
  let offset = 0
  for (let i = 0; i < pageIdx; i++) {
    offset += (props.items[i] as any)?.items?.length || 0
  }
  return offset
}

const formatIntDate = (date: string) => {
  if (!date) return '/'
  // 已经是 "YY/M/D" 格式则直接返回（兼容 New.vue 预格式化场景）
  if (/^\d{2}\/\d{1,2}\/\d{1,2}$/.test(date)) return date
  const d = new Date(date)
  if (isNaN(d.getTime())) return date || '/'
  const year = d.getFullYear().toString().slice(-2)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}/${parseInt(month)}/${parseInt(day)}`
}

/** 分页模式导出：逐页截图 */
const triggerExportPaginated = async () => {
  const container = document.getElementById('factory-production-pdf-content')
  if (!container) throw new Error('生产单PDF内容不存在')

  const pageElements = container.querySelectorAll('.factory-production-pdf-page')
  if (!pageElements.length) throw new Error('没有找到生产单PDF页面元素')

  const html2canvas = (await import('html2canvas')).default
  const jsPDF = (await import('jspdf')).default

  const pdf = new jsPDF('portrait', 'mm', 'a4')
  const pageWidth = pdf.internal.pageSize.getWidth()

  for (let i = 0; i < pageElements.length; i++) {
    const el = pageElements[i] as HTMLElement
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: el.scrollWidth,
      height: el.scrollHeight
    })
    const imgData = canvas.toDataURL('image/png')
    const imgHeight = (canvas.height * pageWidth) / canvas.width
    if (i > 0) pdf.addPage()
    pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight)
  }

  return pdf
}

/** 非分页模式导出：canvas 拼合分页 */
const triggerExportContinuous = async () => {
  const container = document.getElementById('factory-production-pdf-content')
  if (!container) throw new Error('生产单PDF内容不存在')

  const pageEl = container.querySelector('.factory-production-pdf-page') as HTMLElement
  if (!pageEl) throw new Error('没有找到生产单PDF页面元素')

  const headerEl = container.querySelector('.factory-pdf-header-section') as HTMLElement
  const theadEl = container.querySelector('.factory-pdf-table thead') as HTMLElement

  const pageRect = pageEl.getBoundingClientRect()
  const theadDomTop = theadEl
    ? theadEl.getBoundingClientRect().top - pageRect.top
    : 0
  const theadDomBottom = theadEl
    ? theadEl.getBoundingClientRect().bottom - pageRect.top
    : 0

  const rowGroups = container.querySelectorAll('.factory-pdf-product-row-group')
  const summaryTbody = container.querySelector('.factory-pdf-table-summary-row')?.closest('tbody')

  const a4DomHeight = 794 * (297 / 210)
  const dataAreaDomPerPage = a4DomHeight - theadDomBottom - 8

  interface RowInfo {
    domTop: number
    domBottom: number
    height: number
  }
  const rowInfos: RowInfo[] = []
  rowGroups.forEach((rowGroup) => {
    const rect = rowGroup.getBoundingClientRect()
    rowInfos.push({
      domTop: rect.top - pageRect.top,
      domBottom: rect.bottom - pageRect.top,
      height: rect.height
    })
  })

  let summaryDomBottom = 0
  if (summaryTbody) {
    const rect = summaryTbody.getBoundingClientRect()
    summaryDomBottom = rect.bottom - pageRect.top
  }

  const pageRowRanges: { startIdx: number; endIdx: number }[] = []
  let currentStart = 0
  let currentHeight = 0

  for (let i = 0; i < rowInfos.length; i++) {
    const rh = rowInfos[i].height
    if (currentHeight + rh > dataAreaDomPerPage && currentHeight > 0) {
      pageRowRanges.push({ startIdx: currentStart, endIdx: i - 1 })
      currentStart = i
      currentHeight = rh
    } else {
      currentHeight += rh
    }
  }
  if (currentStart < rowInfos.length) {
    pageRowRanges.push({ startIdx: currentStart, endIdx: rowInfos.length - 1 })
  }

  const html2canvas = (await import('html2canvas')).default
  const jsPDF = (await import('jspdf')).default

  const fullCanvas = await html2canvas(pageEl, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: pageEl.scrollWidth,
    height: pageEl.scrollHeight
  })

  const headerCanvas = headerEl
    ? await html2canvas(headerEl, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: headerEl.scrollWidth,
        height: headerEl.scrollHeight
      })
    : null

  const scale = 2
  const theadCanvasHeight = (theadDomBottom - theadDomTop) * scale

  const pdf = new jsPDF('portrait', 'mm', 'a4')
  const pageWidth = pdf.internal.pageSize.getWidth()
  const headerCanvasHeight = headerCanvas ? headerCanvas.height : 0
  const topHeight = headerCanvasHeight + theadCanvasHeight

  if (pageRowRanges.length === 1 && fullCanvas.height <= a4DomHeight * scale) {
    const imgData = fullCanvas.toDataURL('image/png')
    const imgHeight = (fullCanvas.height * pageWidth) / fullCanvas.width
    pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight)
  } else {
    for (let p = 0; p < pageRowRanges.length; p++) {
      if (p > 0) pdf.addPage()

      const { startIdx, endIdx } = pageRowRanges[p]
      const isLastPage = p === pageRowRanges.length - 1

      const dataStartDom = rowInfos[startIdx].domTop
      const dataEndDom = isLastPage && summaryDomBottom > 0
        ? summaryDomBottom
        : rowInfos[endIdx].domBottom

      const dataStartCanvas = dataStartDom * scale
      const dataEndCanvas = dataEndDom * scale
      const dataSliceHeightCanvas = dataEndCanvas - dataStartCanvas

      if (p === 0) {
        const sliceHeight = dataEndCanvas
        const sliceCanvas = document.createElement('canvas')
        sliceCanvas.width = fullCanvas.width
        sliceCanvas.height = sliceHeight
        const ctx = sliceCanvas.getContext('2d')!
        ctx.drawImage(fullCanvas, 0, 0, fullCanvas.width, sliceHeight, 0, 0, fullCanvas.width, sliceHeight)
        const imgData = sliceCanvas.toDataURL('image/png')
        const imgHeight = (sliceHeight * pageWidth) / fullCanvas.width
        pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight)
      } else {
        const pageCanvasHeight = topHeight + dataSliceHeightCanvas
        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = fullCanvas.width
        pageCanvas.height = pageCanvasHeight
        const ctx = pageCanvas.getContext('2d')!

        ctx.drawImage(headerCanvas!, 0, 0)
        ctx.drawImage(
          fullCanvas,
          0, theadDomTop * scale, fullCanvas.width, theadCanvasHeight,
          0, headerCanvasHeight, fullCanvas.width, theadCanvasHeight
        )
        ctx.drawImage(
          fullCanvas,
          0, dataStartCanvas, fullCanvas.width, dataSliceHeightCanvas,
          0, topHeight, fullCanvas.width, dataSliceHeightCanvas
        )

        const imgData = pageCanvas.toDataURL('image/png')
        const imgHeight = (pageCanvasHeight * pageWidth) / fullCanvas.width
        pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight)
      }
    }
  }

  return pdf
}

const triggerExport = async () => {
  try {
    isExporting.value = true
    await nextTick()

    const pdf = props.paginated
      ? await triggerExportPaginated()
      : await triggerExportContinuous()

    const fileName = `生产单_${props.contractInfo.contractName || props.contractInfo.project || '未命名'}.pdf`
    pdf.save(fileName)

    emit('success')
  } catch (error: any) {
    console.error('导出生产单PDF失败:', error)
    emit('error', error?.message || '导出生产单PDF失败')
  } finally {
    isExporting.value = false
  }
}

defineExpose({ triggerExport })
</script>

<style lang="scss" scoped>
.factory-pdf-export-wrapper {
  position: fixed;
  left: -99999px;
  top: 0;
  z-index: -1;
  pointer-events: none;
}

.factory-production-pdf-container {
  width: 794px;
  background: #ffffff;
  font-family: 'Microsoft YaHei', Arial, sans-serif;
  color: #000;
}

.factory-production-pdf-page {
  width: 100%;
  box-sizing: border-box;
  padding: 24px 24px 16px 24px;

  &--fixed {
    height: 1123px;
    overflow: hidden;
    page-break-after: always;
  }
}

.factory-pdf-header-section {
  background-image: url('@/assets/pdfTop.png');
  background-repeat: no-repeat;
  background-size: 100% 100%;
  padding: 16px 16px 8px 16px;
  margin: -8px -8px 0px -8px;
  border-radius: 4px;
}

.factory-pdf-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.factory-pdf-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.factory-pdf-logo-box {
  width: 48px;
  height: 48px;
}

.factory-pdf-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.factory-pdf-header-text {
  display: flex;
  flex-direction: column;
}

.factory-pdf-company-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.factory-pdf-company-name {
  font-size: 22px;
  font-weight: bold;
}

.factory-pdf-order-tag {
  display: inline-block;
  border-radius: 4px;
  border: 1px solid #000;
  font-size: 16px;
  color: #D2475D;
  font-weight: bold;
  line-height: 1;
  width: 60px;
  height: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.factory-pdf-order-info {
  font-size: 16px;
  color: #333;
}

.factory-pdf-strong-text {
  font-weight: 500;
}

.factory-pdf-header-right {
  font-size: 12px;
  color: #000;
}

.factory-pdf-base-info {
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 8px;
  font-size: 16px;
}

.factory-pdf-base-row {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
}

.factory-pdf-base-item {
  display: flex;
  align-items: center;
  width: 50%;
  gap: 6px;
  &.factory-pdf-base-item-time {
    flex-shrink: 0;
  }
}

.factory-pdf-base-item .label {
  min-width: 56px;
  color: #000;
  font-weight: bold;
}

.factory-pdf-base-item .value {
  color: #3E3E3E;
}

.factory-pdf-table-wrapper {
  border-radius: 4px;
  border-left: 1px solid #000;
  border-right: 1px solid #000;
  overflow: hidden;
}

.factory-pdf-table-summary-row {
  height: 36px!important;
  border-bottom: 0!important;
}

.factory-pdf-table-summary-cell {
  padding: 4px 10px;
  height: 36px;
  box-sizing: border-box;
  font-size: 14px;
  text-align: right!important;
  background-color: #EEEEEE;
  font-weight: bold;
}

.factory-pdf-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 14px;
}

.factory-pdf-table th,
.factory-pdf-table td {
  padding: 4px 4px;
  text-align: center;
  vertical-align: middle;
}

.factory-pdf-table th.factory-pdf-text-left,
.factory-pdf-table td.factory-pdf-text-left {
  text-align: left;
}

.factory-pdf-table th:last-child,
.factory-pdf-table td:last-child {
  border-right: none;
}

.factory-pdf-table thead th {
  background-color: #EEEEEE;
  height: 36px;
  font-weight: bold;
  border-bottom: 1px solid #000;
  border-top: 1px solid #000;
}

.factory-pdf-table tbody tr:last-child td {
  border-bottom: 1px solid #000;
}

.factory-pdf-table tbody.factory-pdf-product-row-group {
  height: 100px;
}

.factory-pdf-table tbody tr.factory-pdf-product-desc-row {
  border-bottom: 1px solid #000;
}

.factory-pdf-product-desc-cell {
  padding: 0px 8px 4px 8px !important;
  text-align: left !important;
  vertical-align: top !important;
  line-height: 1.2;
  word-break: break-word;
  word-wrap: break-word;
}

.factory-pdf-desc-label {
  color: #000;
  font-weight: bold;
  margin-right: 6px;
}

.factory-pdf-table td {
  line-height: 1.2;
}

.factory-pdf-table td .factory-pdf-sub-text {
  font-size: 11px;
  color: #666;
  margin-top: 2px;
}
</style>
